/* eslint-disable no-restricted-syntax */
import React, { useEffect, useState } from 'react';
import { calculateHeaderStats } from './utils';
import { Col, Row, Statistic, Spin } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, LoadingOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { isNaN, isNil } from 'lodash';

const bgColour = '#f3f3f3';
const titleColour = 'rgba(0, 0, 0, 0.45)';

const StatsHeader = ({ data, filters = [], currency, fetching, fetched }) => {
    const intl = useIntl();
    const [headerStats, setHeaderStats] = useState({
        roomsSold: 0,
        revenue: 0,
        adr: 0,
        revpar: 0,
        occupancy: 0,
        forecastRevenueDiff: 0,
        budgetRevenueDiff: 0,
        lyspitRevenueDiff: 0,
        lySpit: {
            roomsSold: 0,
            revenue: 0,
            adr: 0,
            revPar: 0,
            occupancy: 0,
        },
        lyFinal: {
            roomsSold: 0,
            revenue: 0,
            adr: 0,
            revPar: 0,
            occupancy: 0,
        },
        budget: {
            roomsSold: 0,
            revenue: 0,
            adr: 0,
            revPar: 0,
            occupancy: 0,
        },
        forecast: {
            roomsSold: 0,
            revenue: 0,
            adr: 0,
            revPar: 0,
            occupancy: 0,
        },
    });

    useEffect(() => {
        setHeaderStats(calculateHeaderStats(data, filters, headerStats));
    }, [data, filters]);

    const formatCurrency = (d, decimalPoints = 2) => {
        if (currency && d) {
            const dataFormatted = parseFloat(d / 100);
            return dataFormatted.toLocaleString('en-GB', {
                style: 'currency',
                currency,
                maximumFractionDigits: decimalPoints,
            });
        }
        if (!d) {
            return ' - ';
        }
        return d;
    };

    const formatPercentage = (value) =>
        intl.formatNumber(value, {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

    const getPrefix = (value) => {
        if (value > 0) {
            return <ArrowUpOutlined style={{ color: '#3f8600' }} />;
        }
        if (value < 0) {
            return <ArrowDownOutlined style={{ color: '#cf1322' }} />;
        }
        return null;
    };

    const getCol = (metric, lyspit, lyfinal, current, span) => {
        let formatter = formatCurrency;
        if (metric === 'Occupancy') {
            formatter = formatPercentage;
        }
        if (metric === 'Rooms') {
            formatter = (val) => val;
        }
        return (
            <Col span={span}>
                <Row>
                    <Col style={{ textAlign: 'right' }} span={12}>
                        <div style={{ display: 'inline-block', textAlign: 'center', marginRight: '10px' }}>
                            <div style={{ color: titleColour }}>LYSPIT</div>
                            <div>
                                {!isNaN(lyspit) && !isNil(lyspit) && formatter(lyspit, 0)}({getPrefix(current - lyspit)}
                                {formatter(current - lyspit, 0)}){(isNaN(lyspit) || isNil(lyspit)) && <>N/A</>}
                            </div>
                        </div>
                    </Col>
                    <Col style={{ textAlign: 'left' }} span={12}>
                        <div style={{ display: 'inline-block', textAlign: 'center', marginLeft: '10px' }}>
                            <div style={{ color: titleColour }}>LYFINAL</div>
                            <div>
                                {!isNaN(lyfinal) && !isNil(lyfinal) && formatter(lyfinal, 0)} (
                                {getPrefix(current - lyfinal)}
                                {formatter(current - lyfinal, 0)}){(isNaN(lyfinal) || isNil(lyfinal)) && <>N/A</>}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Col>
        );
    };

    const getColBudgetForecast = (metric, budget, forecast, current, span) => {
        let formatter = formatCurrency;
        if (metric === 'Occupancy') {
            formatter = formatPercentage;
        }
        if (metric === 'Rooms') {
            formatter = (val) => val;
        }
        return (
            <Col span={span}>
                <Row>
                    <Col style={{ textAlign: 'right' }} span={12}>
                        <div style={{ display: 'inline-block', textAlign: 'center', marginRight: '10px' }}>
                            <div style={{ color: titleColour }}>Budget</div>
                            <div>
                                {!isNaN(budget) && !isNil(budget) && formatter(budget, 0)} (
                                {getPrefix(current - budget)}
                                {formatter(current - budget, 0)}){(isNaN(budget) || isNil(budget)) && <>N/A</>}
                            </div>
                        </div>
                    </Col>
                    <Col style={{ textAlign: 'left' }} span={12}>
                        <div style={{ display: 'inline-block', textAlign: 'center', marginLeft: '10px' }}>
                            <div style={{ color: titleColour }}>Forecast</div>
                            <div>
                                {!isNaN(forecast) && !isNil(forecast) && formatter(forecast, 0)} (
                                {getPrefix(current - forecast)}
                                {formatter(current - forecast, 0)}){(isNaN(forecast) || isNil(forecast)) && <>N/A</>}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Col>
        );
    };

    return (
        <>
            <Row style={{ padding: '10px', backgroundColor: bgColour, textAlign: 'center' }}>
                {fetching && !fetched && (
                    <Col span="24">
                        <Spin indicator={<LoadingOutlined spin />} size="large" />
                    </Col>
                )}
                {!fetching && fetched && (
                    <>
                        <Col span="5">
                            <Statistic size="small" title="Rooms sold" value={headerStats.roomsSold} />
                        </Col>
                        <Col span="7">
                            <Statistic title="Revenue" value={formatCurrency(headerStats.revenue, 0)} />
                        </Col>
                        <Col span="4">
                            <Statistic title="ADR" value={formatCurrency(headerStats.adr)} />
                        </Col>
                        <Col span="4">
                            <Statistic title="RevPAR" value={formatCurrency(headerStats.revpar)} />
                        </Col>
                        <Col span="4">
                            <Statistic title="Occupancy" value={formatPercentage(headerStats.occupancy)} />
                        </Col>
                    </>
                )}
            </Row>
            <Row
                justify="space-evenly"
                style={{ paddingBottom: '10px', backgroundColor: bgColour, textAlign: 'center' }}
            >
                {fetching && !fetched && (
                    <Col>
                        <Spin indicator={<LoadingOutlined spin />} size="large" />
                    </Col>
                )}
                {!fetching &&
                    fetched &&
                    !filters.some((f) => f.property !== 'stayDate') &&
                    headerStats.lySpit &&
                    headerStats.lyFinal && (
                        <>
                            {getCol(
                                'Rooms',
                                headerStats.lySpit.roomsSold,
                                headerStats.lyFinal.roomsSold,
                                headerStats.roomsSold,
                                5,
                            )}
                            {getCol(
                                'Revenue',
                                headerStats.lySpit.revenue,
                                headerStats.lyFinal.revenue,
                                headerStats.revenue,
                                7,
                            )}
                            {getCol('ADR', headerStats.lySpit.adr, headerStats.lyFinal.adr, headerStats.adr, 4)}
                            {getCol(
                                'RevPAR',
                                headerStats.lySpit.revPar,
                                headerStats.lyFinal.revPar,
                                headerStats.revpar,
                                4,
                            )}
                            {getCol(
                                'Occupancy',
                                headerStats.lySpit.occupancy,
                                headerStats.lyFinal.occupancy,
                                headerStats.occupancy,
                                4,
                            )}
                        </>
                    )}
            </Row>
            <Row
                justify="space-evenly"
                style={{ paddingBottom: '10px', backgroundColor: bgColour, textAlign: 'center' }}
            >
                {fetching && !fetched && (
                    <Col>
                        <Spin indicator={<LoadingOutlined spin />} size="large" />
                    </Col>
                )}
                {!fetching &&
                    fetched &&
                    !filters.some((f) => f.property !== 'stayDate') &&
                    headerStats.lySpit &&
                    headerStats.lyFinal && (
                        <>
                            {getColBudgetForecast(
                                'Rooms',
                                headerStats.budget.roomsSold,
                                headerStats.forecast.roomsSold,
                                headerStats.roomsSold,
                                5,
                            )}
                            {getColBudgetForecast(
                                'Revenue',
                                headerStats.budget.revenue,
                                headerStats.forecast.revenue,
                                headerStats.revenue,
                                7,
                            )}
                            {getColBudgetForecast(
                                'ADR',
                                headerStats.budget.adr,
                                headerStats.forecast.adr,
                                headerStats.adr,
                                4,
                            )}
                            {getColBudgetForecast(
                                'RevPAR',
                                headerStats.budget.revPar,
                                headerStats.forecast.revPar,
                                headerStats.revpar,
                                4,
                            )}
                            {getColBudgetForecast(
                                'Occupancy',
                                headerStats.budget.occupancy,
                                headerStats.forecast.occupancy,
                                headerStats.occupancy,
                                4,
                            )}
                        </>
                    )}
            </Row>
        </>
    );
};

export default StatsHeader;
